<template>
  <BaseForm
    :key="ticketId || 'new'"
    :title="formTitle"
    :formData="ticketDeliveryData"
    :fields="fields"
    :submitUrl="submitUrl"
    :method="method"
    :selectedYear="selectedYear"
    :dateRestriction="restrictDates"
    ref="baseForm"
    @form-success="handleSuccess"
    @form-error="handleError"
    @form-changed="onFormChanged"
  />
  <div class="form-holder q-ma-lg q-pl-xl q-pr-xl" style="max-width: 700px">
    <div class="row">
      <div class='col-12 q-mb-md'>
        <q-input filled dense label="Adjusted Weight" readonly v-model="adjustedWeight" />
      </div>
      <div class='col-12'>
        <q-input filled dense label="Customer Charge" readonly v-model="customerCharge" />
      </div>
    </div>
  </div>
</template>

<script>
import BaseForm from './BaseForm.vue';
import axios from 'axios';
import { selectedYear } from '../store/store';

export default {
  components: {
    BaseForm
  },
  props: {
    ticketId: {
      type: Number,
      default: null
    }
  },
  setup() {
    const restrictDates = (date) => {
      const year = selectedYear.value;
      const start = `${year}/01/01`;
      const end = `${year}/12/31`;
      return date >= start && date <= end;
    };

    return {
      selectedYear,
      restrictDates
    };
  },
  data() {
    return {
      ticketDeliveryData: {
        id: '',
        ticket_number: '',
        date: '',
        customer_id: '',
        producer_id: '',
        driver_id: '',
        crop_id: '',
        gross_weight: '',
        tare_weight: '',
        at_moisture: '',
        moisture: '',
        customer_rate: ''
      },
      fields: [
        { model: 'ticket_number', label: 'Ticket Number', required: true, type: 'text', refKey: 'ticketNumberRef' },
        { model: 'date', label: 'Date', required: true, type: 'datetime' },
        { model: 'customer_id', label: 'Customer', required: true, type: 'select', optionsUrl: '/api/v1/customer/customers/', labelField: 'name' },
        { model: 'producer_id', label: 'Producer', required: true, type: 'select', optionsUrl: '/api/v1/producer/producers/', labelField: 'name' },
        { model: 'driver_id', label: 'Driver', required: true, type: 'select', optionsUrl: '/api/v1/driver/drivers/', labelField: 'name' },
        { model: 'crop_id', label: 'Crop', required: true, type: 'select', optionsUrl: '/api/v1/crop/crops/', labelField: 'crop' },
        { model: 'gross_weight', label: 'Gross Weight (lbs)', required: true, type: 'number' },
        { model: 'tare_weight', label: 'Tare Weight (lbs)', required: true, type: 'number' },
        { model: 'moisture', label: 'Moisture (%)', required: true, type: 'number' },
        { model: 'at_moisture', label: 'At Moisture (%)', required: true, type: 'number' },
        { model: 'customer_rate', label: 'Customer Rate ($ per ton)', required: true, type: 'number' }
      ],

      // As in JobForm, we build a { [custId]: { [cropId]: { rate, at_moisture }}} map
      customerRateMap: {},

      // Tracks the currently-selected customer and crop to detect real changes
      currentCustomer: '',
      currentCrop: '',

      // Cannot see data from BaseForm so keep local copy of these variables
      local_gross: 0,
      local_tare: 0,
      local_moisture: 0,
      local_at_moisture: 0,
      local_customer_rate: 0,
    };
  },
  computed: {
    formTitle() {
      return this.ticketId ? `Edit Delivery Ticket` : 'Add Delivery Ticket';
    },
    submitUrl() {
      return this.ticketId 
        ? `/api/v1/ticket_delivery/ticket-deliveries/${this.ticketId}/`
        : '/api/v1/ticket_delivery/ticket-deliveries/';
    },
    method() {
      return this.ticketId ? 'put' : 'post';
    },
    adjustedWeight() {
      let net_weight = this.local_gross - this.local_tare
      let multiplier = (100 - this.local_moisture) / (100 - this.local_at_moisture)
      return ((net_weight * multiplier) / 2000).toFixed(2)
    },
    customerCharge() {
      return (this.adjustedWeight * this.local_customer_rate).toFixed(2)
    },
  },
  mounted() {
    // Load all (customer,crop) rates once
    this.fetchAllCustomerRates();

    // If editing an existing record, load it
    if (this.ticketId) {
      this.loadTicketDeliveryData();
    }
  },
  methods: {
    async fetchAllCustomerRates() {
      try {
        const response = await axios.get('/api/v1/customer_rate/customer_rates/');
        this.customerRateMap = {};

        response.data.forEach(item => {
          const cId = item.cust_id.id;
          const cropId = item.crop_id.id;
          if (!this.customerRateMap[cId]) {
            this.customerRateMap[cId] = {};
          }
          this.customerRateMap[cId][cropId] = {
            rate: item.rate,
            at_moisture: item.at_moisture
          };
        });

        // If there's already a chosen customer/crop, attempt to populate
        this.updateRateFields(this.ticketDeliveryData.customer_id, this.ticketDeliveryData.crop_id);
      } catch (error) {
        console.error('Error fetching customer rates:', error);
      }
    },

    loadTicketDeliveryData() {
      axios.get(`/api/v1/ticket_delivery/ticket-deliveries/${this.ticketId}/`)
        .then(response => {
          this.ticketDeliveryData = {
            ...response.data,
            customer_id: response.data.customer_id.id,
            producer_id: response.data.producer_id.id,
            driver_id: response.data.driver_id.id,
            crop_id: response.data.crop_id.id
          };

          // Update local trackers
          this.currentCustomer = this.ticketDeliveryData.customer_id;
          this.currentCrop = this.ticketDeliveryData.crop_id;

          // Ensure the correct year is selected
          const ticketYear = new Date(this.ticketDeliveryData.date).getFullYear();
          if (ticketYear !== this.selectedYear.value) {
            this.selectedYear.value = ticketYear;
          }

          // Try to populate at_moisture/customer_rate
          this.updateRateFields(this.currentCustomer, this.currentCrop);
        })
        .catch(error => {
          console.error('Error loading ticket delivery data:', error);
        });
    },

    onFormChanged(newVal) {
      // If the user chose a new customer...
      if (newVal.customer_id !== this.currentCustomer) {
        // Merge baseForm data into parent's data
        this.ticketDeliveryData = { ...this.ticketDeliveryData, ...newVal };
        this.currentCustomer = newVal.customer_id;
        // Recompute rate fields
        this.updateRateFields(this.currentCustomer, this.currentCrop);
      }

      // If the user chose a new crop...
      if (newVal.crop_id !== this.currentCrop) {
        this.ticketDeliveryData = { ...this.ticketDeliveryData, ...newVal };
        this.currentCrop = newVal.crop_id;
        this.updateRateFields(this.currentCustomer, this.currentCrop);
      }

      if (newVal.moisture !== this.ticketDeliveryData.moisture) {
        this.ticketDeliveryData = { ...this.ticketDeliveryData, ...newVal };
        this.ticketDeliveryData.moisture = newVal.moisture;
        this.updateRateFields(this.currentCustomer, this.currentCrop);
      }

      //update local gross
      if (newVal.gross_weight !== this.local_gross) {
        if (newVal.gross_weight === '') {
          this.local_gross = 0; 
        } else {
          this.local_gross = newVal.gross_weight;
        }
      }

      //update local tare
      if (newVal.tare_weight !== this.local_tare) {
        if (newVal.tare_weight === '') {
          this.local_tare = 0; 
        } else {
          this.local_tare = newVal.tare_weight;
        }
      }

      //update at_moisture
      if (newVal.at_moisture !== this.local_at_moisture) {
        if (newVal.at_moisture === '') {
          this.local_at_moisture = 0; 
        } else {
          this.local_at_moisture = newVal.at_moisture;
        }
      }

      //update moisture
      if (newVal.moisture !== this.local_moisture) {
        if (newVal.moisture === '') {
          this.local_moisture = 0; 
        } else {
          this.local_moisture = newVal.moisture;
        }
      }

      //update customer_rate
      if (newVal.customer_rate !== this.local_customer_rate) {
        if (newVal.customer_rate === '') {
          this.local_customer_rate = 0; 
        } else {
          this.local_customer_rate = newVal.customer_rate;
        }
      }
    },

    updateRateFields(customerId, cropId) {
      if (
        customerId &&
        cropId &&
        this.customerRateMap[customerId] &&
        this.customerRateMap[customerId][cropId]
      ) {
        const { rate, at_moisture } = this.customerRateMap[customerId][cropId];
        this.ticketDeliveryData.customer_rate = rate;
        this.ticketDeliveryData.at_moisture   = at_moisture;
      } else {
        this.ticketDeliveryData.customer_rate = '';
        this.ticketDeliveryData.at_moisture   = '';
      }
    },

    handleSuccess() {
      this.$q.notify({
        color: 'green',
        position: 'top',
        message: 'Delivery Ticket saved successfully!',
        icon: 'check_circle',
        timeout: 3000
      });
      if (this.method === 'post') {
        this.ticketDeliveryData.ticket_number = '';
        this.ticketDeliveryData.date = '';
        this.ticketDeliveryData.gross_weight = '';
        this.ticketDeliveryData.tare_weight = '';

        this.local_gross = 0;
        this.local_tare = 0; 
        this.local_moisture = 0;
        this.local_at_moisture = 0;
        this.local_customer_rate = 0;

        this.$refs.baseForm.refreshLocalFormData();
        this.$refs.baseForm.focusField('ticketNumberRef');
      }
    },
    handleError() {
      this.$q.notify({
        color: 'red',
        position: 'top',
        message: 'There was a problem saving the delivery ticket!',
        icon: 'error',
        timeout: 3000
      });
    }
  }
};
</script>
